import {objectFromEntries} from '@benchling/js-utils';

import {DataFrame, DataFrameColumnDef, HydratedProperties} from 'data-frames/DataFrameTypes';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {JSONValue} from 'util/Types';

import resolveDataFrameValue, {DataFrameResolutionConfig} from './resolveDataFrameValue';

function resolveRow(
  row: Record<string, JSONValue>,
  columns: Array<DataFrameColumnDef>,
  hydratedProperties: HydratedProperties,
  resolutionConfig: DataFrameResolutionConfig
): Record<string, JSONValue> {
  return objectFromEntries(
    columns.map(({systemName}) => [
      systemName,
      resolveDataFrameValue(row[systemName], hydratedProperties, resolutionConfig),
    ])
  ) as Record<string, JSONValue>;
}

function resolveRows(
  dataFrame: DataFrame,
  resolutionConfig: DataFrameResolutionConfig
): Array<Record<string, JSONValue>> {
  return dataFrame.rows.map((row) =>
    resolveRow(row, dataFrame.columns, dataFrame.hydratedProperties, resolutionConfig)
  );
}

function updateColumnDefinition(
  column: DataFrameColumnDef,
  rows: Array<Record<string, JSONValue>>,
  resolvedRows: Array<Record<string, JSONValue>>
): DataFrameColumnDef {
  if (column.type.leafType === LeafType.JSON) {
    return column;
  }

  const hasResolvedValue = resolvedRows.some(
    (resolvedRow, i) => resolvedRow[column.systemName] !== rows[i][column.systemName]
  );

  if (!hasResolvedValue) {
    return column;
  }

  // If all values are strings, type the new column as TEXT. Else, JSON.
  const allStrings = resolvedRows.every(
    (resolvedRow) =>
      resolvedRow[column.systemName] == null || typeof resolvedRow[column.systemName] === 'string'
  );

  return {
    ...column,
    type: {leafType: allStrings ? LeafType.TEXT : LeafType.JSON, isNullable: column.type.isNullable},
  };
}

function updateColumnDefinitions(
  columns: Array<DataFrameColumnDef>,
  rows: Array<Record<string, JSONValue>>,
  resolvedRows: Array<Record<string, JSONValue>>
): Array<DataFrameColumnDef> {
  return columns.map((column) => updateColumnDefinition(column, rows, resolvedRows));
}

/**
 * Resolve a data frame to a hydrated form, using the API IDs and blob dicts in the data frame's
 * `hydratedProperties`. If any values resolve, the resolved data frame will have different column types.
 *
 * See `resolveDataFrameValue` for how values resolve.
 *
 * Values are resolved without regards to the column type. For example, if a TEXT column contains a hydrated
 * API ID, it can be resolved to the object's name.
 *
 * @param dataFrame The data frame to resolve
 * @param resolutionConfig How to resolve values
 * @deprecated Function is only for legacy data frame use cases where we want to resolve entire data frames
 * for conversion to legacy formats. For new use cases, we should instead only resolve values on access
 * using either resolvedStringAccessor or resolveDataFrameValue directly.
 */
export default function resolveDataFrame(
  dataFrame: DataFrame,
  resolutionConfig: DataFrameResolutionConfig
): DataFrame {
  const resolvedRows = resolveRows(dataFrame, resolutionConfig);

  const resolvedColumns = updateColumnDefinitions(dataFrame.columns, dataFrame.rows, resolvedRows);

  return {
    ...dataFrame,
    columns: resolvedColumns,
    rows: resolvedRows,
  };
}
